import type {FormEventHandler} from 'react';
import {createContext} from 'react';

import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';

import {useInternalForm} from './useInternalForm';

import type {FormContextType, FormValue, HandleFormSubmit} from 'Shared/formHelperTypes';
import type {WithChildren} from 'types/global';

export const FormContext = createContext<FormContextType | null>(null);

interface ComponentProps {
    /**
     * The class name of the component.
     */
    className?: string;
    /**
     * The ID of the component.
     */
    id: string;
    /**
     * The function that handles the change event of the form.
     * It is called with the name of the field and its new value.
     */
    onChange?(name: string, value: FormValue): void;
    /**
     * The function that handles the error event of the form.
     */
    onError?(): void;
    /**
     * The function that handles the reset event of the form.
     */
    onReset?: FormEventHandler<HTMLFormElement>;
    /**
     * The function that handles the submit event of the form.
     */
    onSubmit: HandleFormSubmit;
    /**
     * The test ID used for identifying the component in tests.
     */
    testId?: string;
}

/**
 * Form Component.
 *
 * This component provides a wrapper for form elements and manages the form state,
 * submission, and resetting. It utilizes the `useInternalForm` hook to handle form
 * functionality.
 *
 * @param props           The component props.
 * @param props.children  The children components.
 * @param props.className The CSS class name for the form component.
 * @param props.id        The unique identifier for the form component.
 * @param props.onReset   The callback function triggered when the form is reset.
 * @param props.onSubmit  The callback function triggered when the form is submitted.
 * @param props.testId    The test id for testing purposes.
 *
 * @returns The form component.
 *
 * @example
 * ```tsx
 * const MyForm = () => {
 *   const handleSubmit = (data) => {
 *     // Handle form submission
 *   };
 *
 *   const handleReset = () => {
 *     // Handle form reset
 *   };
 *
 *   return (
 *     <FormComp onSubmit={handleSubmit} onReset={handleReset}>
 *         Children etc.
 *     </FormComp>
 *   );
 * };
 * ```
 */
const Form = observer(({
    children,
    className = '',
    id,
    onChange = () => {},
    onError = () => {},
    onReset = () => {},
    onSubmit,
    testId = 'Form'
}: WithChildren<ComponentProps>) => {
    const {form, formState, handleReset, handleSubmit} = useInternalForm(id, onChange, onError, onReset, onSubmit);

    return (
        <FormElement
            ref={form}
            className={className}
            data-cy={testId}
            id={id}
            noValidate
            onReset={handleReset}
            onSubmit={handleSubmit}
        >
            <FormContext.Provider value={formState}>
                {children}
            </FormContext.Provider>
        </FormElement>
    );
});

Form.displayName = 'Form';

export {Form};

const FormElement = styled.form``;